import type { Node } from './../../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { NodeViewDecorator, ViewerContextualData } from './../../../../ricos-types/index.js';

export class Decorators<T> {
  private decorators: NodeViewDecorator<T>[] = [];

  private textualDecorators: NodeViewDecorator<T>[] = [];

  private context: ViewerContextualData;

  private static readonly defaultPriority = 100;

  private constructor(decorators: NodeViewDecorator<T>[], context: ViewerContextualData) {
    // With ProseMirror, first plugins within an array are executed first.
    // In Tiptap, there is an ability to override plugins,
    // so it feels more natural to run plugins at the end of an array first.
    // That’s why we have to reverse the `extensions` array and sort again
    // based on the `priority` option.
    this.decorators = decorators
      .filter(decorator => !decorator.isText)
      .sort(Decorators.sort)
      .reverse();
    this.textualDecorators = decorators.filter(decorator => decorator.isText);
    this.context = context;
  }

  // sort function must be aligned with Tiptap sort to ensure correct Editor and Viewer decorations order
  // https://github.com/ueberdosis/tiptap/blob/develop/packages/core/src/ExtensionManager.ts#L160
  static sort<T>(a: NodeViewDecorator<T>, b: NodeViewDecorator<T>) {
    const priorityA = a.priority ?? Decorators.defaultPriority;
    const priorityB = b.priority ?? Decorators.defaultPriority;

    if (priorityA > priorityB) {
      return -1;
    }

    if (priorityA < priorityB) {
      return 1;
    }

    return 0;
  }

  static of<T>(decorators: NodeViewDecorator<T>[], context: ViewerContextualData): Decorators<T> {
    return new Decorators(decorators, context);
  }

  getDecorators(): NodeViewDecorator<T>[] {
    return this.decorators;
  }

  merge(decorators: Decorators<T>): Decorators<T> {
    return Decorators.of([...this.decorators, ...decorators.getDecorators()], this.context);
  }

  apply(element: T, node: Node, isRoot: boolean): T {
    return this.decorators.reduce((decorated, decorator) => {
      if (decorator.rule(node)) {
        return decorator.decorate({ node, context: this.context, element: decorated, isRoot });
      }
      return decorated;
    }, element);
  }

  applyTextual(node: Node): T | undefined {
    const decorator = this.textualDecorators.find(decorator => decorator.rule(node));
    return decorator?.decorate({ node, context: this.context, isRoot: false });
  }
}
